import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, PhoneIcon } from 'lucide-react';
import { LandingHero } from '../../components/LandingHero';
import { TrustLocalBand } from '../../components/TrustLocalBand';
import { FaqList } from '../../components/FaqList';
import { serviceOptionValue } from '../../components/HeroLeadForm';
import { findCategory } from '../../data/serviceCategories';
import { acSpecial, business } from '../../data/site';
import { usePageMeta } from '../../hooks/usePageMeta';
const symptoms = ['The A/C is not blowing cold, or only cools some of the time', 'Weak airflow from the vents', 'Warm air when the A/C is switched on', 'Unusual noise when the A/C engages', 'An odor coming from the vents', 'Signs of refrigerant leakage, or a compressor that does not engage'];
const faqs = [{
  question: 'What does the $29.99 A/C pressure test include?',
  answer: 'It covers a comprehensive A/C system inspection, refrigerant level verification, an A/C compressor performance test and an interior vent temperature performance test. The price is $29.99 plus tax and shop fees. Refrigerant, parts and repairs are not included.'
}, {
  question: 'Do you service both R-134a and R-1234yf systems?',
  answer: 'Yes. We service traditional R-134a systems and the newer R-1234yf refrigerant systems used on later-model vehicles.'
}, {
  question: 'Can you just recharge my A/C?',
  answer: 'Low refrigerant generally means it escaped somewhere. Testing first shows whether there is a leak to address rather than repeating the same recharge later.'
}];
export function AcSpecial() {
  const category = findCategory('heating-and-air-conditioning');
  usePageMeta('Auto A/C Repair in Oak Ridge, TN | East End Automotive Repair', 'Automotive A/C repair and diagnostics in Oak Ridge, TN. Complete A/C Pressure Test & System Inspection for $29.99 plus tax and shop fees. Call (865) 206-2279.');
  return <>
      <LandingHero eyebrow="Oak Ridge, Tennessee" title="Auto A/C Repair in Oak Ridge, TN" intro="Vehicle air conditioning that is not cooling can come down to pressure, a leak, an electrical fault or a failed component. We test the system and measure how it is actually performing before recommending any repair." crumbs={[{
      label: 'Specials',
      to: '/specials'
    }, {
      label: 'A/C'
    }]} offer={{
      label: `${acSpecial.name} — $29.99`,
      detail: '$29.99 plus tax and shop fees. Refrigerant, parts and repairs are not included in the test.'
    }} defaultService={serviceOptionValue('heating-and-air-conditioning', 'ac-performance-testing')} sourcePage="special-ac-pressure-test" data-id="element-808" data-mp="2b3522id2gnw1" />

      <section className="w-full bg-white py-14 lg:py-20" data-id="element-809" data-mp="19dj5rkjgoqqe">
        <div className="mx-auto max-w-shell px-5 lg:px-8" data-id="element-810" data-mp="14dws59zq18h2">
          <div className="grid gap-12 md:grid-cols-2 lg:gap-16" data-id="element-811" data-mp="14fho941oozh1">
            <div data-id="element-812" data-mp="3g0qmd2mse45a">
              <h2 className="font-display text-[24px] font-extrabold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[30px]" data-id="element-813" data-mp="3g2bhatd0jp3o">
                Signs your vehicle A/C should be tested
              </h2>
              <ul className="mt-5 space-y-2.5" data-id="element-814" data-mp="3786ssglcqprb">
                {symptoms.map(symptom => <li key={symptom} className="flex items-start gap-2.5 text-[15.5px] leading-snug text-steel/80" data-id="element-815" data-mp="3b8qpjj20jrcx">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" data-id="element-816" data-mp="329vh00bhy56g" />
                    {symptom}
                  </li>)}
              </ul>
              <p className="mt-5 max-w-[560px] text-[14.5px] leading-relaxed text-steel/65" data-id="element-817" data-mp="2sd8xbeuwpk60">
                These symptoms can be related to more than one part of the
                system, which is why testing comes before any repair
                recommendation.
              </p>
            </div>

            <div data-id="element-818" data-mp="3szq08aggw8ec">
              <h2 className="font-display text-[24px] font-extrabold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[30px]" data-id="element-819" data-mp="3sz6iouuqbm0u">
                Automotive A/C and heating services
              </h2>
              <ul className="mt-5 grid gap-2.5 sm:grid-cols-2" data-id="element-820" data-mp="3j21s8itup4e8">
                {(category?.services ?? []).map(service => <li key={service.slug} data-id="element-821" data-mp="3f0qdj8cim3kt">
                    <Link to={`/services/heating-and-air-conditioning/${service.slug}`} className="group flex items-center justify-between gap-2 rounded-lg border border-black/10 bg-chalk px-3.5 py-3 text-[14px] leading-snug text-steel/85 transition-colors duration-150 ease-out hover:border-ink/40 hover:text-ink" data-id="element-822" data-mp="3f1rqxjh0725p">
                      {service.name}
                      <ArrowRightIcon className="h-4 w-4 shrink-0 text-steel/40 group-hover:text-brand-dark" aria-hidden="true" data-id="element-823" data-mp="2ramb34b2c9ve" />
                    </Link>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-chalk py-14 lg:py-20" data-id="element-824" data-mp="1ftjdct6zjq6d">
        <div className="mx-auto max-w-shell px-5 lg:px-8" data-id="element-825" data-mp="1ksmh8ab8bqcr">
          <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-brand-dark" data-id="element-826" data-mp="1kq0a2bs93cv2">
            {acSpecial.angle}
          </p>
          <h2 className="mt-4 max-w-[620px] font-display text-[26px] font-extrabold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[32px]" data-id="element-827" data-mp="s2w1jwty8gej">
            {acSpecial.name}
          </h2>
          <div className="mt-5 flex flex-wrap items-baseline gap-x-4 gap-y-2" data-id="element-828" data-mp="11syslrhifka8">
            <span className="font-display text-[38px] font-extrabold leading-none tracking-tight text-brand-dark" data-id="element-829" data-mp="11v1t55gagvt7">
              $29.99
            </span>
            <span className="text-[14px] text-steel/60" data-id="element-830" data-mp="9bw92ln8wogi">
              Plus tax and shop fees.
            </span>
          </div>

          <div className="mt-10 grid gap-10 md:grid-cols-2 lg:gap-16" data-id="element-831" data-mp="1af13nfkn34w">
            <div data-id="element-832" data-mp="3tf98hw0cayfy">
              <h3 className="text-[12px] font-bold uppercase tracking-[0.14em] text-steel/50" data-id="element-833" data-mp="3tfsf4gkji0eo">
                What is included
              </h3>
              <ul className="mt-4 space-y-3" data-id="element-834" data-mp="6385ga74o9gg">
                {acSpecial.included.map(item => <li key={item} className="flex items-start gap-2.5" data-id="element-835" data-mp="3ei8etmyl8teu">
                    <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-dark" aria-hidden="true" data-id="element-836" data-mp="3ekl5coiewc3i" />
                    <span className="text-[15px] leading-snug text-steel/80" data-id="element-837" data-mp="3oiiude83icx6">
                      {item}
                    </span>
                  </li>)}
              </ul>
              <p className="mt-5 max-w-[520px] text-[14px] leading-relaxed text-steel/60" data-id="element-838" data-mp="13pfafl7cg3tc">
                Refrigerant, recharge, parts and repairs are not included in the
                $29.99 test.
              </p>
            </div>

            <div data-id="element-839" data-mp="2grlidixmco97">
              <h3 className="text-[12px] font-bold uppercase tracking-[0.14em] text-steel/50" data-id="element-840" data-mp="2lo3mqhp1ekl0">
                How the system is inspected
              </h3>
              <ul className="mt-4 border-t border-black/10" data-id="element-841" data-mp="27qf0b5fbn5du">
                {acSpecial.inspectionDetails.map(item => <li key={item.title} className="border-b border-black/10 py-4" data-id="element-842" data-mp="2cpi3slfnl3jp">
                    <h4 className="font-display text-[16px] font-bold tracking-tight text-ink" data-id="element-843" data-mp="30fcht6bpky5j">
                      {item.title}
                    </h4>
                    <p className="mt-1.5 max-w-[520px] text-[14.5px] leading-relaxed text-steel/75" data-id="element-844" data-mp="2qmdh4q2xth5k">
                      {item.copy}
                    </p>
                  </li>)}
              </ul>
            </div>
          </div>

          <a href={business.phoneHref} className="mt-9 inline-flex w-full max-w-full items-center justify-center gap-3 whitespace-normal rounded-md bg-brand px-4 py-4 text-center font-display text-lg font-bold text-white transition-colors duration-150 ease-out hover:bg-brand-light sm:w-auto sm:whitespace-nowrap sm:px-7" data-id="element-845" data-mp="3ja9aplx7msy4">
            <PhoneIcon className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-846" data-mp="1ebjdd6awgg35" />
            Call {business.phone}
          </a>
        </div>
      </section>

      <TrustLocalBand data-id="element-847" data-mp="26zf6k119fpv6" />

      <FaqList faqs={faqs} heading="Vehicle A/C questions" data-id="element-848" data-mp="26zyoviutog9q" />

      <section className="w-full bg-ink py-14 text-white lg:py-16" data-id="element-849" data-mp="26x2rpwqstvmc">
        <div className="mx-auto flex max-w-shell flex-col gap-7 px-5 lg:flex-row lg:items-center lg:justify-between lg:px-8" data-id="element-850" data-mp="220vjfxaxk818">
          <h2 className="max-w-[600px] font-display text-[26px] font-extrabold leading-[1.1] tracking-[-0.02em] sm:text-[32px]" data-id="element-851" data-mp="1x3c38ruo9l6n">
            Call East End about auto A/C repair in Oak Ridge, TN
          </h2>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center lg:shrink-0" data-id="element-852" data-mp="ljrddjf27cko">
            <a href={business.phoneHref} className="inline-flex w-full max-w-full items-center justify-center gap-3 whitespace-normal rounded-md bg-brand px-4 py-4 text-center font-display text-lg font-bold text-white transition-colors duration-150 ease-out hover:bg-brand-light sm:w-auto sm:whitespace-nowrap sm:px-7" data-id="element-853" data-mp="hmlebbw49w06">
              <PhoneIcon className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-854" data-mp="3700u5100tfpz" />
              Call {business.phone}
            </a>
            <Link to="/specials" className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-white/25 px-7 py-4 text-base font-semibold text-white transition-colors duration-150 ease-out hover:border-white/60" data-id="element-855" data-mp="3by28x9qeysvn">
              All Specials
            </Link>
          </div>
        </div>
      </section>
    </>;
}